import { Link } from 'react-router-dom'

import { HeroKicker } from '@/components/marketing/hero-kicker'
import { HeroTitle } from '@/components/marketing/hero-title'
import type {
  NewsroomItem,
} from '@/config/marketing-content/newsroom-items.config'
import type {
  PressRelease,
  PressReleaseBlock,
  PressReleaseInlineNode,
} from '@/config/marketing-content/press-releases.generated'

function formatNewsroomDate(isoDate: string): string {
  const [year, month, day] = isoDate.split('-').map(Number)
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

function InlineNodes({ nodes }: { nodes: PressReleaseInlineNode[] }) {
  return (
    <>
      {nodes.map((node, index) => {
        if (node.type === 'text') {
          return <span key={index}>{node.value}</span>
        }

        return (
          <a
            key={index}
            href={node.href}
            {...(node.href.startsWith('http') || node.href.startsWith('mailto:')
              ? { target: '_blank', rel: 'noopener noreferrer' }
              : {})}
          >
            {node.label}
          </a>
        )
      })}
    </>
  )
}

function PressReleaseBlocks({ blocks }: { blocks: PressReleaseBlock[] }) {
  return (
    <>
      {blocks.map((block, index) => {
        if (block.type === 'paragraph') {
          return (
            <p key={index}>
              <InlineNodes nodes={block.nodes} />
            </p>
          )
        }

        if (block.type === 'blockquote') {
          return (
            <blockquote key={index}>
              <p>
                <InlineNodes nodes={block.nodes} />
              </p>
            </blockquote>
          )
        }

        const Tag = block.level === 2 ? 'h2' : 'h3'
        return (
          <Tag key={index} className="press-release__heading">
            {block.text}
          </Tag>
        )
      })}
    </>
  )
}

export function PressReleaseMeta({ release }: { release: PressRelease }) {
  return (
    <p className="press-release__meta">
      {formatNewsroomDate(release.date)} — {release.location}
    </p>
  )
}

export function PressReleaseLayout({ release }: { release: PressRelease }) {
  return (
    <main className="press-release">
      <div className="marketing-container">
        <header className="press-release__header">
          <div className="hero-stack hero-stack--start">
            <HeroKicker>Press Release</HeroKicker>
            <HeroTitle className="press-release__title">{release.title}</HeroTitle>
            <p className="press-release__subtitle">{release.subtitle}</p>
            <PressReleaseMeta release={release} />
          </div>
        </header>

        <div className="press-release__body press-release__prose">
          <PressReleaseBlocks blocks={release.blocks} />
        </div>

        <footer className="press-release__footer">
          <Link to="/newsroom" className="press-release__back-link">
            Back to Newsroom
          </Link>
        </footer>
      </div>
    </main>
  )
}

export function NewsroomFeaturedCard({ item }: { item: NewsroomItem }) {
  const title = item.external ? (
    <a href={item.href} target="_blank" rel="noopener noreferrer">
      {item.title}
    </a>
  ) : (
    <Link to={item.href}>{item.title}</Link>
  )

  const cta = item.external ? (
    <a href={item.href} target="_blank" rel="noopener noreferrer" className="newsroom-card__cta">
      Read article
    </a>
  ) : (
    <Link to={item.href} className="newsroom-card__cta">
      Read full release
    </Link>
  )

  const meta =
    item.external && item.subtitle
      ? `${item.subtitle} · ${formatNewsroomDate(item.date)}`
      : formatNewsroomDate(item.date)

  return (
    <article className="newsroom-featured">
      <p className="newsroom-card__meta">{meta}</p>
      <h2 className="newsroom-featured__title">{title}</h2>
      {!item.external && item.subtitle ? (
        <p className="newsroom-featured__subtitle">{item.subtitle}</p>
      ) : null}
      {item.excerpt ? <p className="newsroom-featured__excerpt">{item.excerpt}</p> : null}
      {cta}
    </article>
  )
}

function newsroomTableTitle(item: NewsroomItem): string {
  if (item.external && item.subtitle) {
    return `${item.subtitle} - ${item.title}`
  }
  return item.title
}

export function NewsroomTable({ items }: { items: NewsroomItem[] }) {
  if (!items.length) {
    return <p className="newsroom-table__empty">No news yet.</p>
  }

  return (
    <div className="newsroom-table" role="table" aria-label="Newsroom">
      <div className="newsroom-table__header" role="row">
        <span className="newsroom-table__cell newsroom-table__cell--date" role="columnheader">
          Date
        </span>
        <span className="newsroom-table__cell newsroom-table__cell--title" role="columnheader">
          Title
        </span>
      </div>
      {items.map((item) => {
        const rowContent = (
          <>
            <span className="newsroom-table__cell newsroom-table__cell--date" role="cell">
              {formatNewsroomDate(item.date)}
            </span>
            <span className="newsroom-table__cell newsroom-table__cell--title" role="cell">
              {newsroomTableTitle(item)}
            </span>
          </>
        )

        if (item.external) {
          return (
            <a
              key={item.slug}
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              className="newsroom-table__row"
              role="row"
            >
              {rowContent}
            </a>
          )
        }

        return (
          <Link key={item.slug} to={item.href} className="newsroom-table__row" role="row">
            {rowContent}
          </Link>
        )
      })}
    </div>
  )
}

export function PressReleaseFeaturedCard({ release }: { release: PressRelease }) {
  return (
    <NewsroomFeaturedCard
      item={{
        slug: release.slug,
        date: release.date,
        title: release.title,
        href: release.href,
        subtitle: release.subtitle,
        excerpt: release.excerpt,
        location: release.location,
      }}
    />
  )
}

export function PressReleaseTable({ releases }: { releases: PressRelease[] }) {
  return (
    <NewsroomTable
      items={releases.map((release) => ({
        slug: release.slug,
        date: release.date,
        title: release.title,
        href: release.href,
        subtitle: release.subtitle,
        excerpt: release.excerpt,
        location: release.location,
      }))}
    />
  )
}
